/**
 * BRAND TOOLS TAB
 *
 * Verified brand owners' one-stop cleanup tab. Three sections:
 *
 *   1. Brand name — rename the brand (cascades to every collector's
 *      item, polishCatalog, etc.)
 *   2. Collection names — normalize how collectors are labeling your
 *      releases (rename + merge; no admin-only "move to tag")
 *   3. Polishes — per-polish rename, merge, and authoritative tag
 *      editing. Tag saves write to `brands/{brandId}/items/{polishSlug}`,
 *      which the polishCatalog trigger promotes to authoritative over
 *      any collectors' entries.
 *
 * User items are never touched by tag edits. Users' personal collection
 * displays keep whatever they entered; the public catalog reflects the
 * brand's authoritative data.
 *
 * Renamed from BrandPolishesTab — the old "Polishes" label was too
 * generic and hid what these tools actually do.
 */

import { useState, useEffect } from 'react';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  setDoc,
  updateDoc,
  serverTimestamp,
  collectionGroup,
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import type { Brand } from '../../types/brand';
import type { Polish } from '../../types/polish';
import {
  BASE_TYPE_OPTIONS,
  FINISH_OPTIONS,
  HOLO_TYPE_OPTIONS,
  COLOR_SHIFT_OPTIONS,
  PARTICLE_OPTIONS,
  SPECIAL_FEATURE_OPTIONS,
  COLOR_FAMILY_OPTIONS,
  UTILITY_OPTIONS,
  COLOR_CHARACTER_OPTIONS,
} from '../../types';
import type {
  BaseType,
  Finish,
  HoloType,
  ColorShift,
  Particle,
  SpecialFeature,
  ColorFamily,
  Utility,
  ColorCharacter,
} from '../../types';
import TagSelector from '../TagSelector';
import BrandCollectionsPanel from './BrandCollectionsPanel';
import BrandRenamePanel from './BrandRenamePanel';
import BrandSimilarNamesPanel from './BrandSimilarNamesPanel';
import { canonicalizeTags, titleCaseTag } from '../../utils/tagDisplay';
import { getBrandCollections } from '../../services/brandService';
import { slugify } from '../../utils/polishSlug';
import { renamePolish } from '../../services/polishRename';
import { Edit2, Save, RefreshCw, X, ChevronDown, ChevronRight, Search, PenLine, Merge } from 'lucide-react';

interface Props {
  brand: Brand;
  /** Called after the brand rename panel commits a change so the
   *  parent page can refresh its local brand state and other tabs
   *  read the new name. */
  onBrandRenamed?: (newName: string) => void;
}

/** Preview row aggregated from polishCatalog. Sorted by collectorCount
 *  descending so most-visible polishes surface first. */
interface PolishRow {
  slug: string;
  name: string;
  nameSlug: string;
  collectorCount: number;
  colors?: string[];
  colorFamily?: string[];
  baseType?: string;
  finish?: string[];
  holoType?: string[];
  colorShift?: string;
  particles?: string[];
  specialFeatures?: string[];
  utility?: string[];
  colorCharacter?: string[];
  /** Currently-assigned brand collection (from polishCatalog, if the
   *  trigger has aggregated it from a brand item). Undefined when the
   *  brand hasn't assigned this polish to a collection yet. */
  collectionName?: string;
  /** True once a corresponding `brands/{brandId}/items/{nameSlug}` doc
   *  exists — used to badge polishes as "you've claimed these" in the
   *  list. Computed on load; updated on save. */
  hasBrandItem: boolean;
}

/** Per-polish edit-form state. Editing one row at a time keeps the
 *  form logic simple and matches how brand owners typically correct
 *  data (one polish at a time, not bulk). */
interface EditState {
  slug: string;
  /** Assigned brand collection name (from your Collections tab). Empty
   *  string means "no collection assigned" — writes an unset value to
   *  the brand item on save, so the polish stays out of any collection
   *  grouping publicly. */
  collectionName: string;
  baseType: BaseType | undefined;
  finish: Finish[];
  holoType: HoloType[];
  colorShift: ColorShift | undefined;
  particles: Particle[];
  specialFeatures: SpecialFeature[];
  colorFamily: ColorFamily[];
  utility: Utility[];
  colorCharacter: ColorCharacter[];
}

/** Minimal shape we care about from `getBrandCollections`. Just the
 *  name — that's what writes to `brands/{brandId}/items/{slug}` as
 *  `collectionName`. Full collection details live on the Collections
 *  tab; here we just need the picker options. */
interface BrandCollectionOption {
  id: string;
  name: string;
}

/** Canonical enum list used by the display preview under each row. Same
 *  order as the polish detail page's labeled grid so brand owners see
 *  the same set of chip categories they'd see publicly. */
const DISPLAY_ROWS: Array<{
  key: keyof PolishRow;
  label: string;
  enumValues: readonly string[];
  chipClass: string;
}> = [
  { key: 'colorFamily', label: 'Color family', enumValues: COLOR_FAMILY_OPTIONS, chipClass: 'bg-slate-200 text-slate-700' },
  { key: 'baseType', label: 'Base', enumValues: BASE_TYPE_OPTIONS, chipClass: 'bg-fuchsia-100 text-fuchsia-700' },
  { key: 'finish', label: 'Finish', enumValues: FINISH_OPTIONS, chipClass: 'bg-purple-100 text-purple-700' },
  { key: 'holoType', label: 'Holo type', enumValues: HOLO_TYPE_OPTIONS, chipClass: 'bg-indigo-100 text-indigo-700' },
  { key: 'colorShift', label: 'Color shift', enumValues: COLOR_SHIFT_OPTIONS, chipClass: 'bg-emerald-100 text-emerald-700' },
  { key: 'particles', label: 'Particles', enumValues: PARTICLE_OPTIONS, chipClass: 'bg-rose-100 text-rose-700' },
  { key: 'specialFeatures', label: 'Special', enumValues: SPECIAL_FEATURE_OPTIONS, chipClass: 'bg-sky-100 text-sky-700' },
  { key: 'utility', label: 'Utility', enumValues: UTILITY_OPTIONS, chipClass: 'bg-amber-100 text-amber-700' },
  { key: 'colorCharacter', label: 'Character', enumValues: COLOR_CHARACTER_OPTIONS, chipClass: 'bg-pink-100 text-pink-700' },
];

export default function BrandToolsTab({ brand, onBrandRenamed }: Props) {
  const [rows, setRows] = useState<PolishRow[]>([]);
  const [loading, setLoading] = useState(true);
  /** Brand's official collections, fetched once on mount. Powers the
   *  collection picker in the polish edit form. Empty when the brand
   *  hasn't defined any collections yet — dropdown shows a hint
   *  pointing at the Collections tab. */
  const [brandCollections, setBrandCollections] = useState<BrandCollectionOption[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState<EditState | null>(null);
  const [savingSlug, setSavingSlug] = useState<string | null>(null);
  const [rebuildingSlug, setRebuildingSlug] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{ slug: string; text: string; kind: 'ok' | 'err' } | null>(null);
  /** Rename flow state — when set to a slug, the row shows an inline
   *  name input (draft in renameDraft). Cancel clears both. */
  const [renamingSlug, setRenamingSlug] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState('');
  /** Merge flow state — when set to a source slug, the row shows a
   *  dropdown of other polishes in this brand as the merge target. */
  const [mergingSlug, setMergingSlug] = useState<string | null>(null);
  const [mergeTargetSlug, setMergeTargetSlug] = useState('');
  const [busySlug, setBusySlug] = useState<string | null>(null);

  // Fetch the brand's official collections once. Feeds the collection
  // picker in the polish edit form. Cheap query — brand collections
  // are a bounded subcollection.
  useEffect(() => {
    if (!brand?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const cols = await getBrandCollections(brand.id);
        if (!cancelled) {
          setBrandCollections(cols.map((c) => ({ id: c.id, name: c.name })));
        }
      } catch (err) {
        console.error('getBrandCollections failed:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [brand?.id]);

  useEffect(() => {
    if (!brand?.slug) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const brandSlug = brand.slug;
        // 1) Fetch all polishCatalog docs for this brand.
        const catalogSnap = await getDocs(
          query(collection(db, 'polishCatalog'), where('brandSlug', '==', brandSlug))
        );
        const catalog: PolishRow[] = catalogSnap.docs.map((d) => {
          const data = d.data() as Partial<Polish> & Record<string, unknown>;
          return {
            slug: d.id,
            name: typeof data.name === 'string' ? data.name : d.id,
            nameSlug: typeof data.nameSlug === 'string' ? data.nameSlug : d.id.split('__')[1] || '',
            collectorCount: typeof data.collectorCount === 'number' ? data.collectorCount : 0,
            colors: Array.isArray(data.colors) ? (data.colors as string[]) : undefined,
            colorFamily: Array.isArray(data.colorFamily) ? (data.colorFamily as string[]) : undefined,
            baseType: typeof data.baseType === 'string' ? data.baseType : undefined,
            finish: Array.isArray(data.finish) ? (data.finish as string[]) : undefined,
            holoType: Array.isArray(data.holoType) ? (data.holoType as string[]) : undefined,
            colorShift: typeof data.colorShift === 'string' ? data.colorShift : undefined,
            particles: Array.isArray(data.particles) ? (data.particles as string[]) : undefined,
            specialFeatures: Array.isArray(data.specialFeatures) ? (data.specialFeatures as string[]) : undefined,
            utility: Array.isArray(data.utility) ? (data.utility as string[]) : undefined,
            colorCharacter: Array.isArray(data.colorCharacter) ? (data.colorCharacter as string[]) : undefined,
            collectionName: typeof data.collectionName === 'string' ? data.collectionName : undefined,
            hasBrandItem: false,
          };
        });

        // 2) For each row, check whether a corresponding brand item
        // exists. We batch by listing the brand's items subcollection
        // once — much cheaper than N per-slug lookups.
        const brandItemsSnap = await getDocs(
          collection(db, 'brands', brand.id, 'items')
        );
        const claimedNameSlugs = new Set<string>();
        for (const it of brandItemsSnap.docs) {
          const data = it.data() as Record<string, unknown>;
          const ns = typeof data.nameSlug === 'string'
            ? data.nameSlug
            : slugify(typeof data.name === 'string' ? data.name : '');
          if (ns) claimedNameSlugs.add(ns);
        }
        for (const r of catalog) {
          if (claimedNameSlugs.has(r.nameSlug)) r.hasBrandItem = true;
        }

        // 3) Sort: unclaimed with collectors first (needs your attention),
        // then claimed, then by collectorCount desc, then by name.
        catalog.sort((a, b) => {
          if (a.hasBrandItem !== b.hasBrandItem) return a.hasBrandItem ? 1 : -1;
          if (a.collectorCount !== b.collectorCount) return b.collectorCount - a.collectorCount;
          return a.name.localeCompare(b.name);
        });

        if (!cancelled) setRows(catalog);
      } catch (err) {
        if (!cancelled) setError(`Failed to load polishes: ${(err as Error).message}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [brand?.slug, brand?.id]);

  const filtered = filter.trim()
    ? rows.filter((r) => r.name.toLowerCase().includes(filter.trim().toLowerCase()))
    : rows;

  const toggleExpand = (slug: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };

  const beginEdit = async (row: PolishRow) => {
    // Prefill from existing brand item if one exists — otherwise seed
    // from the current polishCatalog aggregation as a starting point.
    let seed: Partial<EditState> = {
      collectionName: row.collectionName ?? '',
      baseType: row.baseType as BaseType | undefined,
      finish: (row.finish ?? []) as Finish[],
      holoType: (row.holoType ?? []) as HoloType[],
      colorShift: row.colorShift as ColorShift | undefined,
      particles: (row.particles ?? []) as Particle[],
      specialFeatures: (row.specialFeatures ?? []) as SpecialFeature[],
      colorFamily: (row.colorFamily ?? []) as ColorFamily[],
      utility: [] as Utility[],
      colorCharacter: [] as ColorCharacter[],
    };
    if (row.hasBrandItem) {
      try {
        const biSnap = await getDoc(doc(db, 'brands', brand.id, 'items', row.nameSlug));
        if (biSnap.exists()) {
          const bi = biSnap.data() as Record<string, unknown>;
          seed = {
            collectionName: typeof bi.collectionName === 'string' ? bi.collectionName : '',
            baseType: typeof bi.baseType === 'string' ? (bi.baseType as BaseType) : undefined,
            finish: (Array.isArray(bi.finish) ? bi.finish : []) as Finish[],
            holoType: (Array.isArray(bi.holoType) ? bi.holoType : []) as HoloType[],
            colorShift: typeof bi.colorShift === 'string' ? (bi.colorShift as ColorShift) : undefined,
            particles: (Array.isArray(bi.particles) ? bi.particles : []) as Particle[],
            specialFeatures: (Array.isArray(bi.specialFeatures) ? bi.specialFeatures : []) as SpecialFeature[],
            colorFamily: (Array.isArray(bi.colorFamily) ? bi.colorFamily : []) as ColorFamily[],
            utility: (Array.isArray(bi.utility) ? bi.utility : []) as Utility[],
            colorCharacter: (Array.isArray(bi.colorCharacter) ? bi.colorCharacter : []) as ColorCharacter[],
          };
        }
      } catch { /* fall through to catalog seed */ }
    }
    setEditing({
      slug: row.slug,
      collectionName: seed.collectionName ?? '',
      baseType: seed.baseType,
      finish: seed.finish ?? [],
      holoType: seed.holoType ?? [],
      colorShift: seed.colorShift,
      particles: seed.particles ?? [],
      specialFeatures: seed.specialFeatures ?? [],
      colorFamily: seed.colorFamily ?? [],
      utility: seed.utility ?? [],
      colorCharacter: seed.colorCharacter ?? [],
    });
    // Auto-expand the row being edited.
    setExpanded((prev) => new Set(prev).add(row.slug));
  };

  const saveEdit = async () => {
    if (!editing) return;
    const row = rows.find((r) => r.slug === editing.slug);
    if (!row) return;
    setSavingSlug(editing.slug);
    setFeedback(null);
    try {
      // Write to `brands/{brandId}/items/{nameSlug}` — the trigger sees
      // any doc in this subcollection as a brand item and promotes its
      // tag values to authoritative on the polishCatalog aggregation.
      const ref = doc(db, 'brands', brand.id, 'items', row.nameSlug);
      const payload: Record<string, unknown> = {
        brandId: brand.id,
        name: row.name,
        nameSlug: row.nameSlug,
        slug: row.slug,
        updatedAt: serverTimestamp(),
      };
      // Only include fields with values so we don't overwrite existing
      // brand-item data (e.g. photos, description) with undefined.
      // Empty string on collectionName means "no collection assigned"
      // — deliberately WRITE an empty string here so the trigger picks
      // it up and unsets polishCatalog.collectionName; leaving the
      // field out would preserve any prior brand assignment.
      payload.collectionName = editing.collectionName;
      if (editing.baseType) payload.baseType = editing.baseType;
      if (editing.finish.length > 0) payload.finish = editing.finish;
      if (editing.holoType.length > 0) payload.holoType = editing.holoType;
      if (editing.colorShift) payload.colorShift = editing.colorShift;
      if (editing.particles.length > 0) payload.particles = editing.particles;
      if (editing.specialFeatures.length > 0) payload.specialFeatures = editing.specialFeatures;
      if (editing.colorFamily.length > 0) payload.colorFamily = editing.colorFamily;
      if (editing.utility.length > 0) payload.utility = editing.utility;
      if (editing.colorCharacter.length > 0) payload.colorCharacter = editing.colorCharacter;

      // Existence check to decide setDoc merge vs. createdAt bump.
      const existing = await getDoc(ref);
      if (!existing.exists()) {
        payload.status = 'active';
        payload.createdAt = serverTimestamp();
      }
      await setDoc(ref, payload, { merge: true });

      // Optimistically update the row + close the editor. The trigger
      // will re-aggregate polishCatalog in the background; the row's
      // preview will reflect it on next load.
      setRows((prev) => prev.map((r) => r.slug === editing.slug
        ? {
            ...r,
            collectionName: editing.collectionName || undefined,
            baseType: editing.baseType,
            finish: editing.finish,
            holoType: editing.holoType,
            colorShift: editing.colorShift,
            particles: editing.particles,
            specialFeatures: editing.specialFeatures,
            colorFamily: editing.colorFamily,
            utility: editing.utility,
            colorCharacter: editing.colorCharacter,
            hasBrandItem: true,
          }
        : r
      ));
      setEditing(null);
      setFeedback({ slug: row.slug, text: 'Saved — polishCatalog will re-aggregate shortly.', kind: 'ok' });
    } catch (err) {
      setFeedback({ slug: editing.slug, text: `Save failed: ${(err as Error).message}`, kind: 'err' });
    } finally {
      setSavingSlug(null);
    }
  };

  const beginRename = (row: PolishRow) => {
    setRenamingSlug(row.slug);
    setRenameDraft(row.name);
    setMergingSlug(null);
    setEditing(null);
  };

  const runRename = async (row: PolishRow) => {
    const toName = renameDraft.trim();
    if (!toName) {
      setFeedback({ slug: row.slug, text: 'Enter a new name first.', kind: 'err' });
      return;
    }
    if (toName === row.name) {
      setFeedback({ slug: row.slug, text: 'New name is identical to current name — nothing to do.', kind: 'err' });
      return;
    }
    // Confirm since the cascade is wide (user items + confirms +
    // creations + polishCatalog). Brand owners are trusted but a
    // double-check protects against typos.
    if (!window.confirm(
      `Rename "${row.name}" → "${toName}"?\n\nThis renames the polish across every collector who owns it, plus polishCatalog. Nobody's collection is deleted — just the canonical name updates.`
    )) return;
    setBusySlug(row.slug);
    setFeedback(null);
    try {
      const summary = await renamePolish({
        fromBrand: brand.name,
        fromName: row.name,
        toBrand: brand.name,
        toName,
      });
      setFeedback({
        slug: row.slug,
        text: `Renamed. ${summary.itemsUpdated} item${summary.itemsUpdated === 1 ? '' : 's'} updated, ${summary.confirmsMigrated} confirm${summary.confirmsMigrated === 1 ? '' : 's'} migrated.`,
        kind: 'ok',
      });
      // Optimistically update the row's display and slug.
      setRows((prev) => prev.map((r) => r.slug === row.slug
        ? { ...r, name: toName, nameSlug: slugify(toName), slug: summary.toSlug }
        : r
      ));
      setRenamingSlug(null);
      setRenameDraft('');
    } catch (err) {
      setFeedback({ slug: row.slug, text: `Rename failed: ${(err as Error).message}`, kind: 'err' });
    } finally {
      setBusySlug(null);
    }
  };

  const beginMerge = (row: PolishRow) => {
    setMergingSlug(row.slug);
    setMergeTargetSlug('');
    setRenamingSlug(null);
    setEditing(null);
  };

  const runMerge = async (row: PolishRow) => {
    const target = rows.find((r) => r.slug === mergeTargetSlug);
    if (!target) {
      setFeedback({ slug: row.slug, text: 'Pick a target polish to merge into.', kind: 'err' });
      return;
    }
    if (target.slug === row.slug) {
      setFeedback({ slug: row.slug, text: 'Cannot merge a polish into itself.', kind: 'err' });
      return;
    }
    if (!window.confirm(
      `Merge "${row.name}" into "${target.name}"?\n\nEvery user's item under "${row.name}" gets moved to "${target.name}" (name + slug rewritten). The source polishCatalog entry is deleted. This is not reversible — check the target polish's name is correct.`
    )) return;
    setBusySlug(row.slug);
    setFeedback(null);
    try {
      const summary = await renamePolish({
        fromBrand: brand.name,
        fromName: row.name,
        toBrand: brand.name,
        toName: target.name,
      });
      setFeedback({
        slug: row.slug,
        text: `Merged into "${target.name}". ${summary.itemsUpdated} item${summary.itemsUpdated === 1 ? '' : 's'} moved.`,
        kind: 'ok',
      });
      // Drop the source row from state (its polishCatalog doc should be deleted).
      setRows((prev) => prev.filter((r) => r.slug !== row.slug));
      setMergingSlug(null);
      setMergeTargetSlug('');
    } catch (err) {
      setFeedback({ slug: row.slug, text: `Merge failed: ${(err as Error).message}`, kind: 'err' });
    } finally {
      setBusySlug(null);
    }
  };

  /** Force the polishCatalog trigger to re-fire by writing a timestamp
   *  to one user item under this polish. Same mechanism as Tag Pusher's
   *  Rebuild button — needed when a save didn't actually change values
   *  (e.g. all fields were already set) but the polishCatalog doc is
   *  stale because triggers didn't run. */
  const rebuild = async (row: PolishRow) => {
    setRebuildingSlug(row.slug);
    setFeedback(null);
    try {
      const snap = await getDocs(
        query(collectionGroup(db, 'items'), where('slug', '==', row.slug))
      );
      // Find ANY item (brand OR user) — writing to it fires the trigger.
      const target = snap.docs[0]?.ref;
      if (!target) {
        setFeedback({ slug: row.slug, text: 'No items found for this polish. Nothing to touch.', kind: 'err' });
        return;
      }
      await updateDoc(target, { _lastAggregateTouch: serverTimestamp() });
      setFeedback({ slug: row.slug, text: 'Rebuild triggered — refresh in ~5s to see updated data.', kind: 'ok' });
    } catch (err) {
      setFeedback({ slug: row.slug, text: `Rebuild failed: ${(err as Error).message}`, kind: 'err' });
    } finally {
      setRebuildingSlug(null);
    }
  };

  if (loading) {
    return (
      <div className="glass-card p-6 text-glass-secondary font-body">
        Loading polishes…
      </div>
    );
  }
  if (error) {
    return (
      <div className="glass-card p-6 text-red-300 font-body">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Tab intro — plain-language explanation of what these tools do.
          Brand owners aren't devs; leading with "what problem does this
          fix" makes the sections feel purposeful rather than dense. */}
      <div className="glass-card p-6">
        <h1 className="text-xl font-bold text-glass mb-1 font-body">Brand Tools</h1>
        <p className="text-sm text-glass-secondary font-body">
          Fix the data that shows publicly for your brand. Anything you
          set here becomes authoritative — collectors' personal
          collections stay untouched. Three tools:
        </p>
        <ul className="text-sm text-glass-secondary font-body mt-2 space-y-1 list-disc list-inside">
          <li>
            <span className="text-glass">Brand name</span> — fix casing or spelling; cascades to every collector's items.
          </li>
          <li>
            <span className="text-glass">Similar brand names</span> — claim polishes attributed to variant spellings, or dismiss ones that aren't yours.
          </li>
          <li>
            <span className="text-glass">Collection names</span> — normalize how collectors label your releases.
          </li>
          <li>
            <span className="text-glass">Items</span> — rename, merge duplicates, and set authoritative tag data per item (polishes, plates, tools, etc.).
          </li>
        </ul>
      </div>

      {/* Section 1: Brand name */}
      <BrandRenamePanel brand={brand} onRenamed={onBrandRenamed} />

      {/* Section 1b: Similar name variants — surface polishes attributed
          to brand names that look like yours, so the owner can claim
          them onto their canonical brand or dismiss them. */}
      <BrandSimilarNamesPanel brand={brand} />

      {/* Section 2: Collections */}
      <BrandCollectionsPanel brand={brand} />

      {/* Section 3: Polishes */}
      <div className="glass-card p-6">
      <div className="flex items-start justify-between mb-4 gap-4">
        <div>
          <h2 className="text-lg font-semibold text-glass font-body">Items</h2>
          <p className="text-sm text-glass-secondary font-body">
            One row per item attributed to your brand. Rename, merge
            duplicates, or set the authoritative tag data. Rows with
            "Community-aggregated" haven't been claimed yet — they're
            showing whatever data collectors entered.
          </p>
          <p className="text-xs text-glass-muted font-body mt-1">
            Currently lists polishes only — the aggregated catalog for
            plates/tools/etc. is on the roadmap. Non-polish items can
            still be edited on their own detail modals in the meantime.
          </p>
        </div>
      </div>

      {/* Filter */}
      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-muted" />
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter polishes…"
            className="w-full pl-9 pr-3 py-2 bg-slate-900 border border-slate-600 rounded text-glass placeholder:text-glass-muted"
          />
        </div>
        <div className="mt-2 text-xs text-glass-muted font-body">
          {filtered.length} of {rows.length} polishes.{' '}
          {rows.filter((r) => !r.hasBrandItem).length} not yet claimed as
          official records.
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12 bg-slate-800/40 rounded-xl border border-slate-600 text-glass-secondary font-body">
          {rows.length === 0
            ? 'No polishes attributed to your brand yet.'
            : 'No polishes match your filter.'}
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((row) => {
            const isOpen = expanded.has(row.slug);
            const isEditing = editing?.slug === row.slug;
            const isSaving = savingSlug === row.slug;
            const isRebuilding = rebuildingSlug === row.slug;
            const rowFeedback = feedback?.slug === row.slug ? feedback : null;
            return (
              <div
                key={row.slug}
                className="bg-slate-800/40 border border-slate-600 rounded-lg overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() => toggleExpand(row.slug)}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-800/60 transition-default text-left"
                >
                  {isOpen ? (
                    <ChevronDown className="w-4 h-4 text-glass-muted flex-shrink-0" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-glass-muted flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-glass font-medium truncate font-body">
                      {row.name}
                    </div>
                    <div className="text-xs text-glass-muted font-body">
                      {row.collectorCount} collector{row.collectorCount === 1 ? '' : 's'} · slug: <code>{row.slug}</code>
                    </div>
                  </div>
                  {row.hasBrandItem ? (
                    <span className="px-2 py-0.5 text-[10px] rounded-full bg-emerald-500/20 text-emerald-300 flex-shrink-0">
                      Authoritative
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 text-[10px] rounded-full bg-amber-500/20 text-amber-300 flex-shrink-0">
                      Community-aggregated
                    </span>
                  )}
                </button>

                {isOpen && (
                  <div className="border-t border-slate-700 p-4 bg-slate-900/40 space-y-4">
                    {!isEditing && (
                      <>
                        {/* Current tag preview */}
                        <div className="grid grid-cols-[100px_1fr] gap-x-3 gap-y-2">
                          {DISPLAY_ROWS.map((cfg) => {
                            const raw = row[cfg.key];
                            const rawArr: string[] = Array.isArray(raw)
                              ? (raw as string[])
                              : typeof raw === 'string' && raw
                              ? [raw]
                              : [];
                            const canon = canonicalizeTags(rawArr, cfg.enumValues);
                            if (canon.length === 0) return null;
                            return (
                              <div key={cfg.label} className="contents">
                                <div className="text-xs text-glass-muted text-right pt-1 font-body">
                                  {cfg.label}
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                  {canon.map((v) => (
                                    <span
                                      key={v}
                                      className={`px-2 py-0.5 text-xs rounded ${cfg.chipClass}`}
                                    >
                                      {titleCaseTag(v)}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={() => beginEdit(row)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-purple-600 text-white rounded hover:bg-purple-700 font-body"
                          >
                            <Edit2 className="w-4 h-4" /> Edit tags
                          </button>
                          <button
                            onClick={() => beginRename(row)}
                            disabled={busySlug === row.slug}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-slate-700 text-glass rounded hover:bg-slate-600 disabled:opacity-50 font-body"
                            title="Rename this polish. Cascades to every collector's item + confirms + creations."
                          >
                            <PenLine className="w-4 h-4" /> Rename
                          </button>
                          <button
                            onClick={() => beginMerge(row)}
                            disabled={busySlug === row.slug}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-slate-700 text-glass rounded hover:bg-slate-600 disabled:opacity-50 font-body"
                            title="Merge this polish into another one — collectors' items get moved to the target."
                          >
                            <Merge className="w-4 h-4" /> Merge
                          </button>
                          <button
                            onClick={() => rebuild(row)}
                            disabled={isRebuilding}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-slate-700 text-glass rounded hover:bg-slate-600 disabled:opacity-50 font-body"
                            title="Force the polishCatalog trigger to re-fire. Use if a save should have propagated but the data still looks stale."
                          >
                            <RefreshCw className={`w-4 h-4 ${isRebuilding ? 'animate-spin' : ''}`} />
                            Rebuild
                          </button>
                        </div>

                        {/* Inline rename UI. Only appears when Rename
                            was clicked on this specific row. */}
                        {renamingSlug === row.slug && (
                          <div className="bg-slate-800/60 border border-slate-600 rounded p-3 space-y-2">
                            <label className="block text-xs text-glass-muted font-body">
                              New name for this polish
                            </label>
                            <input
                              type="text"
                              value={renameDraft}
                              onChange={(e) => setRenameDraft(e.target.value)}
                              disabled={busySlug === row.slug}
                              className="w-full bg-slate-900 border border-slate-600 rounded px-3 py-1.5 text-glass"
                              placeholder="e.g. Willow in the Wind"
                              autoFocus
                            />
                            <div className="flex gap-2">
                              <button
                                onClick={() => runRename(row)}
                                disabled={busySlug === row.slug || !renameDraft.trim()}
                                className="px-3 py-1.5 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50 font-body"
                              >
                                {busySlug === row.slug ? 'Renaming…' : 'Confirm rename'}
                              </button>
                              <button
                                onClick={() => { setRenamingSlug(null); setRenameDraft(''); }}
                                disabled={busySlug === row.slug}
                                className="px-3 py-1.5 text-sm bg-slate-700 text-glass rounded hover:bg-slate-600 disabled:opacity-50 font-body"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}

                        {/* Inline merge UI — pick a target polish from
                            this brand. Same brand only; the service is
                            called with the same brand string. */}
                        {mergingSlug === row.slug && (
                          <div className="bg-slate-800/60 border border-slate-600 rounded p-3 space-y-2">
                            <label className="block text-xs text-glass-muted font-body">
                              Merge into which polish?
                            </label>
                            <select
                              value={mergeTargetSlug}
                              onChange={(e) => setMergeTargetSlug(e.target.value)}
                              disabled={busySlug === row.slug}
                              className="w-full bg-slate-900 border border-slate-600 rounded px-3 py-1.5 text-glass"
                            >
                              <option value="">— Choose a polish —</option>
                              {rows
                                .filter((r) => r.slug !== row.slug)
                                .map((r) => (
                                  <option key={r.slug} value={r.slug}>
                                    {r.name} ({r.collectorCount} collector{r.collectorCount === 1 ? '' : 's'})
                                  </option>
                                ))}
                            </select>
                            <div className="flex gap-2">
                              <button
                                onClick={() => runMerge(row)}
                                disabled={busySlug === row.slug || !mergeTargetSlug}
                                className="px-3 py-1.5 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50 font-body"
                              >
                                {busySlug === row.slug ? 'Merging…' : 'Confirm merge'}
                              </button>
                              <button
                                onClick={() => { setMergingSlug(null); setMergeTargetSlug(''); }}
                                disabled={busySlug === row.slug}
                                className="px-3 py-1.5 text-sm bg-slate-700 text-glass rounded hover:bg-slate-600 disabled:opacity-50 font-body"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    {isEditing && editing && (
                      <div className="space-y-3">
                        <div className="text-xs text-glass-muted font-body">
                          Set the tag data that should appear on this polish's
                          public detail page. Blank categories fall back to
                          the community aggregation from user items.
                        </div>

                        {/* Brand collection assignment. Pulls from
                            brands/{brandId}/collections. If empty, a
                            hint sends the brand owner to the Collections
                            tab to define them first. */}
                        <div className="bg-slate-800/40 border border-slate-600 rounded p-3">
                          <label className="block text-xs font-medium text-glass-muted mb-1 font-body">
                            Collection assignment
                          </label>
                          {brandCollections.length === 0 ? (
                            <div className="text-xs text-glass-secondary font-body">
                              No official collections defined yet. Add
                              them on the <strong>Collections</strong> tab
                              and they'll appear here as options.
                            </div>
                          ) : (
                            <select
                              value={editing.collectionName}
                              onChange={(e) =>
                                setEditing((prev) =>
                                  prev ? { ...prev, collectionName: e.target.value } : prev
                                )
                              }
                              className="w-full bg-slate-900 border border-slate-600 rounded px-3 py-2 text-glass"
                            >
                              <option value="">— No collection —</option>
                              {brandCollections.map((c) => (
                                <option key={c.id} value={c.name}>
                                  {c.name}
                                </option>
                              ))}
                            </select>
                          )}
                          <div className="text-[10px] text-glass-muted mt-1">
                            This is the canonical collection shown on the
                            public polish page + brand-page grouping. Overrides anything collectors typed.
                          </div>
                        </div>

                        <TagSelector
                          baseType={editing.baseType}
                          finish={editing.finish}
                          holoType={editing.holoType}
                          colorShift={editing.colorShift}
                          particles={editing.particles}
                          specialFeatures={editing.specialFeatures}
                          colorFamily={editing.colorFamily}
                          utility={editing.utility}
                          colorCharacter={editing.colorCharacter}
                          onChange={(categoryKey, value) => {
                            setEditing((prev) => prev
                              ? { ...prev, [categoryKey]: value } as EditState
                              : prev
                            );
                          }}
                          defaultExpanded={[]}
                        />
                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={saveEdit}
                            disabled={isSaving}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50 font-body"
                          >
                            <Save className="w-4 h-4" /> {isSaving ? 'Saving…' : 'Save'}
                          </button>
                          <button
                            onClick={() => setEditing(null)}
                            disabled={isSaving}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-slate-700 text-glass rounded hover:bg-slate-600 disabled:opacity-50 font-body"
                          >
                            <X className="w-4 h-4" /> Cancel
                          </button>
                        </div>
                      </div>
                    )}

                    {rowFeedback && (
                      <div
                        className={`text-xs font-body px-3 py-2 rounded ${
                          rowFeedback.kind === 'ok'
                            ? 'bg-emerald-500/10 text-emerald-300'
                            : 'bg-red-500/10 text-red-300'
                        }`}
                      >
                        {rowFeedback.text}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      </div>
    </div>
  );
}
